import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { WritingStyle, IllustrationStyle, OrderItem, ToggleCategory } from './types';
import StyleCard from './StyleCard';
import { ModelCatalogEntry } from './useModelRegistry';

interface StyleCardGridProps {
  orderedItems: OrderItem[];
  allItems: (WritingStyle | IllustrationStyle)[];
  categoryKey: string;
  styleType: 'writing' | 'illustration';
  toggleCategory: ToggleCategory;
  isMostPopularCategory: boolean;
  loading: boolean;
  expandedRows: Set<number>;
  onToggle: (id: number, currentActive: boolean, category: ToggleCategory) => void;
  onEdit: (style: WritingStyle | IllustrationStyle, type: 'writing' | 'illustration') => void;
  onDelete: (id: number, type: 'writing' | 'illustration') => void;
  onExpandToggle: (id: number) => void;
  onUpdateModelPreference?: (id: number, category: ToggleCategory, modelPreference: string | null) => void;
  modelCatalog?: ModelCatalogEntry[];
}

const StyleCardGrid: React.FC<StyleCardGridProps> = ({
  orderedItems,
  allItems,
  categoryKey,
  styleType,
  toggleCategory,
  isMostPopularCategory,
  loading,
  expandedRows,
  onToggle,
  onEdit,
  onDelete,
  onExpandToggle,
  onUpdateModelPreference,
  modelCatalog,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', p: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={120}
            sx={{ borderRadius: '10px' }}
          />
        ))}
      </Box>
    );
  }

  // Compute active positions
  const activeOrdered = orderedItems.filter(i => i.isActive);

  // Split into in-grid (positions 1-4 among active) and below-grid
  const inGridIds = new Set(activeOrdered.slice(0, 4).map(i => i.id));

  return (
    <Box sx={{ p: 2, overflowY: 'auto', minHeight: 0 }}>
      <SortableContext items={orderedItems.map(i => i.id)} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          {orderedItems.map((orderItem, idx) => {
            const fullItem = allItems.find(s => s.id === orderItem.id);
            if (!fullItem) return null;

            const activePosition = activeOrdered.findIndex(i => i.id === orderItem.id);
            const inGrid = inGridIds.has(orderItem.id);
            const position = idx + 1;
            const isMostPopular = activePosition === 0 && isMostPopularCategory;

            // Insert divider before first non-in-grid item
            const prevItem = idx > 0 ? orderedItems[idx - 1] : null;
            const prevInGrid = prevItem ? inGridIds.has(prevItem.id) : true;
            const showDivider = !inGrid && prevInGrid && idx > 0;

            return (
              <React.Fragment key={orderItem.id}>
                {showDivider && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.68rem',
                        color: '#94a3b8',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                    >
                      Not in app grid — drag card up or into a grid slot
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                  </Box>
                )}
                <StyleCard
                  item={fullItem}
                  position={position}
                  inGrid={inGrid}
                  isMostPopular={isMostPopular}
                  isExpanded={expandedRows.has(fullItem.id)}
                  styleType={styleType}
                  toggleCategory={toggleCategory}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onExpandToggle={onExpandToggle}
                  onUpdateModelPreference={onUpdateModelPreference}
                  modelCatalog={modelCatalog}
                />
              </React.Fragment>
            );
          })}
        </Box>
      </SortableContext>
    </Box>
  );
};

export default StyleCardGrid;
